import React from 'react';
import * as Routes from 'routes';
import PropTypes from 'prop-types';
import I18n from 'modules/i18n';
import { TCIDropdown } from '@/components/ui/Dropdown';
import { TCITooltip } from '@/components/ui/Tooltip';
import styles from './ManagePrograms.module.scss';
import { ContactAdminsModalProvider } from './ContactAdminsModal';
import { DisabledMessage, TooltipMessage } from './DisabledMessages';

// AIDEV-NOTE: TCIDropdown handles WAI-ARIA keyboard nav, focus restore, click-outside, escape,
// and portal positioning. Disabled menu items are tooltip-wrapped via TCITooltip — TCIDropdown's
// custom Item (no Radix MenuItem) doesn't blur on pointerleave, so the tooltip survives mouse
// movement into its content. Disabled items keep aria-disabled + an onClick guard so keyboard
// users can still arrow-focus them and surface the disabled-reason tooltip.

const DropdownItemLink = React.forwardRef(({
  className, faIcon, href, label, ...rest
}, ref) => (
  <a ref={ref} className={`tw-whitespace-nowrap ${className || ''}`.trim()} href={href} {...rest}>
    <i aria-hidden="true" className={`fa ${faIcon}`} /> {label}
  </a>
));

DropdownItemLink.displayName = 'DropdownItemLink';

DropdownItemLink.propTypes = {
  className: PropTypes.string,
  faIcon: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

DropdownItemLink.defaultProps = {
  className: undefined,
};

const ManagePrograms = ({
  hasTrials, adminDashPath, userType, addProgramsDisabled, removeProgramsDisabled, getAdminInfoPath,
  addProgramsPath, manuallyManaged, adminProgramsPath, removeProgramsPath, trialStaffer,
}) => (
  <ContactAdminsModalProvider>
    <TCIDropdown>
      <TCIDropdown.Trigger asChild>
        <button className="btn btn--outline-purple" type="button">
          {I18n.t('manage_programs')} <i aria-hidden="true" className={`fa fa-caret-down ${styles.caret}`} />
        </button>
      </TCIDropdown.Trigger>
      <TCIDropdown.Content width="unset">
        {!trialStaffer && (
          <TCITooltip disabled={!addProgramsDisabled}>
            <TCITooltip.Trigger asChild>
              <TCIDropdown.Item asChild disabled={addProgramsDisabled}>
                <DropdownItemLink
                  faIcon={addProgramsDisabled ? 'fa-lock' : 'fa-plus'}
                  href={addProgramsPath}
                  label={I18n.t('add_programs')}
                />
              </TCIDropdown.Item>
            </TCITooltip.Trigger>
            <TCITooltip.Content>
              <DisabledMessage
                adminDashPath={adminDashPath}
                adminProgramsPath={adminProgramsPath}
                getAdminInfoPath={getAdminInfoPath}
                manuallyManaged={manuallyManaged}
                userType={userType}
              />
            </TCITooltip.Content>
          </TCITooltip>
        )}
        {!trialStaffer && (
          <TCITooltip disabled={!removeProgramsDisabled}>
            <TCITooltip.Trigger asChild>
              <TCIDropdown.Item asChild disabled={removeProgramsDisabled}>
                <DropdownItemLink
                  faIcon={removeProgramsDisabled ? 'fa-lock' : 'fa-trash'}
                  href={removeProgramsPath}
                  label={I18n.t('remove_programs')}
                />
              </TCIDropdown.Item>
            </TCITooltip.Trigger>
            <TCITooltip.Content>
              <TooltipMessage
                adminDashPath={adminDashPath}
                getAdminInfoPath={getAdminInfoPath}
                userType={userType}
              />
            </TCITooltip.Content>
          </TCITooltip>
        )}
        {trialStaffer && (
          <TCIDropdown.Item asChild>
            <DropdownItemLink
              faIcon="fa-plus"
              href={Routes.trials_teacher_programs_path()}
              label={I18n.t('add_thirty_day_trial')}
            />
          </TCIDropdown.Item>
        )}
        {hasTrials && (
          <TCIDropdown.Item asChild>
            <DropdownItemLink
              faIcon="fa-trash"
              href={Routes.remove_teacher_programs_path()}
              label={I18n.t('remove_trials')}
            />
          </TCIDropdown.Item>
        )}
      </TCIDropdown.Content>
    </TCIDropdown>
  </ContactAdminsModalProvider>
);

ManagePrograms.propTypes = {
  addProgramsDisabled: PropTypes.bool,
  addProgramsPath: PropTypes.string.isRequired,
  adminDashPath: PropTypes.string.isRequired,
  adminProgramsPath: PropTypes.string.isRequired,
  getAdminInfoPath: PropTypes.string.isRequired,
  hasTrials: PropTypes.bool.isRequired,
  manuallyManaged: PropTypes.bool,
  removeProgramsDisabled: PropTypes.bool,
  removeProgramsPath: PropTypes.string.isRequired,
  trialStaffer: PropTypes.bool.isRequired,
  userType: PropTypes.string.isRequired,
};

ManagePrograms.defaultProps = {
  addProgramsDisabled: false,
  manuallyManaged: false,
  removeProgramsDisabled: false,
};

export default ManagePrograms;
